import React from 'react'
import clsx from 'clsx'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { CircleRating, RichText, Typography } from '@homeviews/shared-ui'
import type { Review } from '@homeviews/models'
import { getReviewValue } from '../../helpers/getReviewValue'
import { getSustainabilityValues } from '../../helpers/getSustainabilityValues'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {},
    title: {
      marginBottom: theme.spacing(1.2),
    },
    ratings: {
      display: 'flex',
      marginBottom: theme.spacing(1.25),
    },
    ratingsTitle: {
      marginTop: 20,
      marginBottom: 15,
    },
    ratingsTitleAdditional: {
      marginTop: 0,
    },
    rating: {
      marginRight: theme.spacing(5),
    },
    replyTitle: {
      marginTop: theme.spacing(1.5),
      marginBottom: `15px!important`,
      color: `${theme.colors.primary}!important`,
    },
    reviewQuestions: {},
    reviewQuestion: {
      marginTop: 15,
    },
    reviewQuestionContent: {},
    reviewQuestionTitle: {
      marginBottom: 5,
    },
    reviewContent: {},
    reviewQuestionAdditional: {},
    additionalRatings: {
      '&:empty': {
        display: 'none',
      },
    },
    sustainabilityItem: {
      marginBottom: 5,
    },
    sustainabilityItems: {},
    sustainabilityTitle: {
      marginBottom: 10,
    },
    author: {
      fontSize: 18,
      marginBottom: 20,
      fontWeight: 500,
    },
  }),
)

interface IPreviewReview extends Review {
  _PREV_DISABLED?: boolean
  _NEXT_DISABLED?: boolean
}
interface IProps {
  review: IPreviewReview
}

const ReviewPreview: React.FC<IProps> = ({ review }) => {
  const classes = useStyles()

  const {
    title,
    content,
    rating,
    rating_facilities,
    rating_design,
    rating_location,
    rating_value,
    rating_management,
    location_comments,
    landlord_comments,
    developer_comments,
    one_thing,
    one_change,
    problem_response,
    best_feature,
    additions,
    reason,
    disclosure,
    tenant_rating_service,
    tenant_rating_repairs,
    tenant_rating_additional,
    owner_rating_service,
    owner_rating_timetable,
    owner_rating_quality,
    owner_rating_aftersales,
    owner_recommends,
    tenant_recommends,
    display_name,
  } = review

  const sustainability_values = getSustainabilityValues(review)

  const isBtr = tenant_recommends === 'yes' || tenant_recommends === 'no'

  const showAdditionalRatings =
    getReviewValue('tenant_rating_service', review) ||
    getReviewValue('tenant_rating_repairs', review) ||
    getReviewValue('tenant_rating_additional', review) ||
    getReviewValue('owner_rating_service', review) ||
    getReviewValue('owner_rating_timetable', review) ||
    getReviewValue('owner_rating_quality', review) ||
    getReviewValue('owner_rating_aftersales', review)

  return (
    <div className={classes.root}>
      <Typography variant="h1" className={classes.title}>
        {title}
      </Typography>
      <Typography className={classes.author}>
        by <strong>{display_name}</strong>
      </Typography>
      <RichText content={content} className={classes.reviewContent} />
      <div className={classes.reviewQuestions}>
        {!!location_comments && location_comments !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Please tell us what you like and dislike about this area:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={location_comments}
            />
          </div>
        )}

        {!!one_thing && one_thing !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Please share one thing (or more) which you wish you had known
              before you moved in:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={one_thing}
            />
          </div>
        )}

        {!!one_change && one_change !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              If there is one thing you would change about your flat or property
              what would it be:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={one_change}
            />
          </div>
        )}

        {!!problem_response && problem_response !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              How has the building management responded to any problems or
              issues you have raised:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={problem_response}
            />
          </div>
        )}

        {!!best_feature && best_feature !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              What is the best feature of your home:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={best_feature}
            />
          </div>
        )}

        {!!disclosure && disclosure !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Please disclose any commercial relationship to this development or
              developer:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={disclosure}
            />
          </div>
        )}

        {!!additions && additions !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Are there any facilities you would like added, or that you have
              but do not use:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={additions}
            />
          </div>
        )}

        {!!reason && reason !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Why did you choose this development:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={reason}
            />
          </div>
        )}

        {!!tenant_recommends && tenant_recommends !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Would you recommend your landlord?
            </Typography>
            <Typography className={classes.reviewQuestionContent}>
              {getReviewValue('tenant_recommends', review)}
            </Typography>
          </div>
        )}

        {!!owner_recommends && owner_recommends !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Would you recommend your developer?
            </Typography>
            <Typography className={classes.reviewQuestionContent}>
              {getReviewValue('owner_recommends', review)}
            </Typography>
          </div>
        )}

        {!!landlord_comments && landlord_comments !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Landlord comments:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={landlord_comments}
            />
          </div>
        )}

        {!!developer_comments && developer_comments !== 'N/A' && (
          <div className={classes.reviewQuestion}>
            <Typography
              variant="h4"
              component="p"
              className={classes.reviewQuestionTitle}
            >
              Developer comments:
            </Typography>
            <RichText
              className={classes.reviewQuestionContent}
              content={developer_comments}
            />
          </div>
        )}
      </div>
      <Typography className={classes.ratingsTitle} variant="h3">
        Your ratings
      </Typography>
      <div className={classes.ratings}>
        <CircleRating
          className={classes.rating}
          title="Overall"
          value={rating}
        />
        <CircleRating
          className={classes.rating}
          title="Facilities"
          value={rating_facilities}
        />
        <CircleRating
          className={classes.rating}
          title="Design"
          value={rating_design}
        />
        <CircleRating
          className={classes.rating}
          title="Location"
          value={rating_location}
        />
        <CircleRating
          className={classes.rating}
          title="Value"
          value={rating_value}
        />
        <CircleRating
          className={classes.rating}
          title="Management"
          value={rating_management}
        />
      </div>

      {showAdditionalRatings && (
        <Typography
          className={clsx(classes.ratingsTitle, classes.ratingsTitleAdditional)}
          variant="h3"
        >
          {isBtr
            ? 'Build to Rent additional questions'
            : 'New Build Owner additional questions'}
        </Typography>
      )}

      <div className={clsx(classes.ratings, classes.additionalRatings)}>
        {getReviewValue('tenant_rating_service', review) && (
          <CircleRating
            className={classes.rating}
            title="Customer Service"
            value={tenant_rating_service}
          />
        )}
        {getReviewValue('tenant_rating_repairs', review) && (
          <CircleRating
            className={classes.rating}
            title="Repairs and Maintenance"
            value={tenant_rating_repairs}
          />
        )}
        {getReviewValue('tenant_rating_additional', review) && (
          <CircleRating
            className={classes.rating}
            title="Additional Services"
            value={tenant_rating_additional}
          />
        )}

        {getReviewValue('owner_rating_service', review) && (
          <CircleRating
            className={classes.rating}
            title="Customer Service"
            value={owner_rating_service}
          />
        )}
        {getReviewValue('owner_rating_timetable', review) && (
          <CircleRating
            className={classes.rating}
            title="Delivery Timetable"
            value={owner_rating_timetable}
          />
        )}
        {getReviewValue('owner_rating_quality', review) && (
          <CircleRating
            className={classes.rating}
            title="Delivery Quality"
            value={owner_rating_quality}
          />
        )}
        {getReviewValue('owner_rating_aftersales', review) && (
          <CircleRating
            className={classes.rating}
            title="After Sales Care"
            value={owner_rating_aftersales}
          />
        )}
      </div>

      {!!sustainability_values.length && (
        <div className={classes.sustainabilityItems}>
          <Typography variant="h4" className={classes.sustainabilityTitle}>
            Which environmentally sustainable features are the most important to
            you when choosing a new home?
          </Typography>
          {sustainability_values.map((label: string, index: number) => (
            <Typography key={index} className={classes.sustainabilityItem}>
              {label}
            </Typography>
          ))}
        </div>
      )}
    </div>
  )
}

export default ReviewPreview
